import { Injectable } from '@angular/core';
import { HttpWrapper } from '../../../shared';
import { DateRange } from '../../../shared/components/date-range.component';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as L from 'leaflet';
import _ from 'lodash';
import { DateTimeHelpers } from '../../../shared/helpers/date-time.helpers';
import moment from 'moment';
import { Router } from '@angular/router';
import { VesselReportV202205 } from './noon-reports-v202205.service';
import { NoonReportServiceSpa } from 'src/app/generated/services/transportation/reports/noonSpa';
import { NoonReportLastReceivedv202005, NoonReportV202005, SailingStatusv202005 } from 'src/app/generated/services/transportation/reports/noon';
import { LatLngExpression } from 'leaflet';
export { NoonReportV202005 } from 'src/app/generated/services/transportation/reports/noon';

export function getSailingBunkerConsumption(report: NoonReportV202005, sailingStatus: SailingStatusv202005) {
  if (report.noonReportBunkerStatus?.sailingBunkerConsumption) {
    return report.noonReportBunkerStatus.sailingBunkerConsumption
      .find(r => r.status === sailingStatus);
  }
}

export interface SelectedLocation {
  locations: NoonReportV202005[];
  selected: boolean;
  vesselName: string;
  vesselImo: string;
}

export interface HighchartsSeries {
  name: string;
  type: string;
  visible: boolean;
  data: [number, number][];
}

export interface HighchartsData {
  id: string;
  divId: string;
  series: HighchartsSeries[];
}

@Injectable({
  providedIn: 'root'
})
export class NoonReportsService {

  private mapDisplayFixBounds: [number, number][] = [[30.43, 37.28], [36.86, 133.6], [-4.7, 109.69]];

  constructor(private _httpWrapper: HttpWrapper, private _router: Router, private _noonReportService: NoonReportServiceSpa) { }

  static noonReporsAnalyseMapper(value: string) {
    const mapper = new Map<string, string>([
      ['hfoCons', 'HFO Cons'],
      ['hfoRob', 'HFO ROB'],
      ['lsfoCons', 'LSFO Cons'],
      ['lsfoRob', 'LSFO ROB'],
      ['mgoCons', 'MGO Cons'],
      ['mgoRob', 'MGO ROB'],
      ['lsmgoCons', 'LSMGO Cons'],
      ['lsmgoRob', 'LSMGO ROB'],
      ['hfoBalance', 'HFO Balance'],
      ['lsfoBalance', 'LSFO Balance'],
      ['mgoBalance', 'MGO Balance'],
      ['lsmgoBalance', 'LSMGO Balance'],
    ]);
    return mapper.get(value);
  }

  getNoonReportsV202005Between(dr: DateRange): Observable<NoonReportV202005[]> {
    // return this._httpWrapper.get<NoonReportV202005[]>(`vessels/noon-reports-v20200521/${dr.startDate.getTime()}/${dr.endDate.getTime()}`);
    return from(this._noonReportService.getNoonReportsV202005Between({
      startTime: dr.startDate.getTime(),
      endTime: dr.endDate.getTime(),
    })).pipe(map(r => r.reports));
  }

  getVesselNoonV202005(vesselImo: string, dr: DateRange): Observable<NoonReportV202005[]> {
    return from(this._noonReportService.getNoonReportsV202005Between({
      startTime: dr.startDate.getTime(),
      endTime: dr.endDate.getTime()
    })).pipe(map(n => n.reports.filter(r => r.vesselImo === vesselImo)));
  }

  deleteNoonV202005(noon: NoonReportV202005) {
    return this._httpWrapper.put('vessels/noon-report-v202005/delete', noon);
  }

  getVesselLatestReportV2020(): Observable<NoonReportV202005[]> {
    return from(this._noonReportService.getLatestNoonReportsV202005({})).pipe(map(n => n.reports));
  }

  getVesselLatestReport(): Observable<any[]> {
    return this._httpWrapper.get<any[]>('vessels/noon-reports-v20200521/latest-report');
  }

  getVesselLatestReportExcel() {
    return this._httpWrapper.download('vessels/noon-reports-v20200521/latest-report/excel');
  }

  getArrivalsAndDepartures2022(dr: DateRange): Observable<VesselReportV202205[]> {
    const reportTypes = ['arrival', 'departure'];
    return from(this._noonReportService.getNoonReportsV202205Between({
      startTime: dr.startDate.getTime(),
      endTime: dr.endDate.getTime(),
    }).then(r => r.reports.filter(r => reportTypes.some(t => t === r.reportType.toLocaleLowerCase()))));
  }

  getLocationBounds(reports: SelectedLocation[]) {
    const points: LatLngExpression[] = reports
      .filter(s1 => s1.selected)
      .flatMap(s1 => s1.locations)
      .map(n1 => [n1.position!.geoCoordinate!.latitude, n1.position!.geoCoordinate!.longitude]
      );
    if (points.length <= 1) {
      return new L.LatLngBounds(this.mapDisplayFixBounds);
    } else {
      return new L.LatLngBounds(points);
    }
  }

  groupLocations(reports: NoonReportV202005[]): SelectedLocation[] {
    return _.chain(reports)
      .groupBy(item => `${item.vesselName}§${item.vesselImo}`)
      .map((items, key) => {
        const vessel = key.split('§')[0];
        const imo = key.split('§')[1];
        return {
          locations: items.sort((a, b) => a.date!.timestamp - b.date!.timestamp),
          selected: true,
          vesselName: vessel,
          vesselImo: imo
        };
      }).value();
  }

  getLocationPopupContent(noon: NoonReportV202005) {
    const isLaden = noon.voyageDetails?.isLaden;
    let ladenStatus = isLaden ? 'LADEN' : 'BALLAST';
    if (isLaden === null || isLaden === undefined) {
      ladenStatus = 'Unknown';
    }
    return `
<a class="btn-link text-decoration-none" id="name"><h5>${noon.vesselName}</h5></a>
<table>
<tr>
<td>Date &nbsp;</td>
<td>${DateTimeHelpers.formatLocalDate(noon.date!.timestamp)}</td>
</tr>
<tr>
<tr>
<td>Report &nbsp;</td>
<td>${noon.reportType}</td>
</tr>
<tr>
<td>From</td>
<td>${noon.voyageDetails?.source}</td>
</tr>
<tr>
<td>To</td>
<td>${noon.voyageDetails?.destination}</td>
</tr>
<tr>
<td>ETA</td>
<td>${DateTimeHelpers.formatLocalDate(noon.voyageDetails!.eta)}</td>
</tr><tr>
<tr>
<td>Speed</td>
<td>${Math.round(noon.speed!.averageSpeed * 100) / 100}</td>
</tr><tr>
<td>Cargo &nbsp; &nbsp;</td>
<td>${ladenStatus}</td>
</tr>
</table>
`;
  }

  getVesselLocationMarkerLayer(noon: NoonReportV202005) {
    const isLaden = noon.voyageDetails?.isLaden;
    let colour = isLaden ? '#159653' : '#DD4B39';
    if (isLaden === null || isLaden === undefined) {
      colour = 'grey';
    }
    const diff = moment().diff(moment.unix(noon.date!.timestamp / 1000), 'hours');
    const opacity = diff < 24 ? 1.0 : 0.5;
    const markerOptions: L.MarkerOptions = {
      icon: L.divIcon({
        html: `<i class="fa fa-ship" style="font-size:20px; color:${colour}; opacity: 0.9">
               </i><br><i style="color: ${colour}">${noon.vesselName}</i>`,
        iconSize: [30, 30],
        className: 'myDivIcon'
      }),
      opacity
    };

    const marker = L.marker([noon.position!.geoCoordinate!.latitude, noon.position!.geoCoordinate!.longitude], markerOptions)
      .bindPopup(this.getLocationPopupContent(noon), { maxWidth: 500 }).on('popupopen', (a) => {
        const popUp = a.target.getPopup();
        popUp.getElement().querySelector('#name').addEventListener('click', () => {
          this._router.navigate(['vessel', noon.vesselImo]);
        });
      });
    return marker;
  }

  downloadConsumptionsReport(startTime: number, endTime: number) {
    return this._httpWrapper.download(`vessels/noon-reports-v20200521/bunker-consumption/${startTime}/${endTime}`);
  }

  downloadReports(startTime: number, endTime: number) {
    return from(this._noonReportService.getNoonReportsV202005Excel({
      startTimestamp: startTime,
      endTimestamp: endTime
    })).pipe(map((r) => {
      console.log(`received results: ${r.fileName}`);
      const bytes = new Uint8Array(r.excelFile);
      return {
        blob: new Blob([bytes], { type: 'application/octet-stream' }),
        fileName: r.fileName,
      };
    }));
  }

  async importV202005Report(timestamp: number, fileList: FileList): Promise<NoonReportV202005> {
    const excelContent = await fileList.item(0)!.arrayBuffer();

    return this._noonReportService.updateNoonReportV202005({
      timestamp: timestamp,
      excelContent: new Uint8Array(excelContent)
    });
  }

  calculateTypeConsumption(noonReport: NoonReportV202005, previousReport?: NoonReportV202005) {
    const sailingConsumptions = noonReport.noonReportBunkerStatus!.sailingBunkerConsumption
      .flatMap(c => c.bunkerConsumption);
    const otherConsumptions = noonReport.noonReportBunkerStatus!.otherBunkerConsumption
      .flatMap(c => c.bunkerConsumption);

    const robMap = new Map<string, number>(
      noonReport.noonReportBunkerStatus!.rob.map(data => [data.bunkerType, data.quantityMt])
    );

    const previousRobMap = new Map<string, number>(
      previousReport?.noonReportBunkerStatus!.rob.map(data => [data.bunkerType, data.quantityMt])
    );
    const lastReceivedRobMap = new Map<string, NoonReportLastReceivedv202005>(
      noonReport.noonReportBunkerStatus!.lastReceived.map(data => [data.receivedType, data])
    );

    const analyse = _(_.groupBy(sailingConsumptions.concat(otherConsumptions), (c) => c.bunkerType)).map((cons, key) => {
      const consumption = Math.round(_.sumBy(cons, c => c.quantityMt) * 100) / 100;
      const rob = robMap.get(key);
      const sum = Math.round(((rob || 0) + consumption) * 100) / 100;
      const prevRob = (previousRobMap.get(key)) ? previousRobMap.get(key) : 0;
      const reportDate = DateTimeHelpers.formatUTCDate(noonReport.date!.timestamp);
      const lastReceivedDate = lastReceivedRobMap.get(key) ? DateTimeHelpers.formatUTCDate(lastReceivedRobMap.get(key)!.timestamp) : '';
      const preDate = DateTimeHelpers.formatUTCDate(moment(noonReport.date!.timestamp).subtract(1, 'days').unix() * 1000);
      const diff =
        (prevRob && (sum - prevRob) > 10 &&
        (lastReceivedDate === reportDate || preDate === lastReceivedDate)) ?
          sum - prevRob - lastReceivedRobMap.get(key)!.quantityMt : (prevRob) ? sum - prevRob : 0;
      return {
        bunkerType: key,
        consumption,
        rob,
        previousRob: prevRob,
        sum,
        diff: Math.round(diff * 100) / 100,
        lastReceived: (lastReceivedDate === reportDate) ? lastReceivedRobMap.get(key)!.quantityMt : '',
        consumptionChart: [noonReport.date!.timestamp, consumption],
        robChart: [noonReport.date!.timestamp, robMap.get(key)],
      };
    }).value();
    return _.keyBy(analyse, data => data.bunkerType);
  }

  calculateAuxiliarySystemOilConsumption(noonReport: NoonReportV202005, previousReport: NoonReportV202005) {
    const previousRob = (previousReport) ? previousReport.lubOilStatus!.auxiliarySystemOilRob : noonReport.lubOilStatus!.auxiliarySystemOilRob;
    const sum = noonReport.lubOilStatus!.auxiliarySystemOilConsumption + noonReport.lubOilStatus!.auxiliarySystemOilRob;
    const lastReceived = noonReport.lubOilStatus!.lastReceived.find(l => l.receivedType === 'AE SYS Oil');
    const lastReceivedDate = lastReceived ? DateTimeHelpers.formatUTCDate(lastReceived.timestamp) : '';
    const reportDate = DateTimeHelpers.formatUTCDate(noonReport.date!.timestamp);
    const preDate = DateTimeHelpers.formatUTCDate(moment(noonReport.date!.timestamp).subtract(1, 'days').unix() * 1000);
    const diff = ((sum - previousRob) > 10 && (lastReceivedDate === reportDate || preDate === lastReceivedDate)) ?
      sum - previousRob - lastReceived!.quantityMt : sum - previousRob;
    return {
      previousRob,
      consumption: noonReport.lubOilStatus!.auxiliarySystemOilConsumption,
      rob: noonReport.lubOilStatus!.auxiliarySystemOilRob,
      sum,
      diff: Math.round(diff * 100) / 100,
      lastReceivedQt: (lastReceivedDate === reportDate || preDate === lastReceivedDate) ? lastReceived!.quantityMt : ''
    };
  }

  calculateMainEngineCylinderOilConsumption(noonReport: NoonReportV202005, previousReport: NoonReportV202005) {
    const previousRob = (previousReport) ? previousReport.lubOilStatus!.mainEngineCylinderOilRob : noonReport.lubOilStatus!.mainEngineCylinderOilRob;
    const sum = noonReport.lubOilStatus!.mainEngineCylinderOilConsumption + noonReport.lubOilStatus!.mainEngineCylinderOilRob;
    const lastReceived = noonReport.lubOilStatus!.lastReceived.find(l => l.receivedType === 'ME CYL Oil');
    const lastReceivedDate = lastReceived ? DateTimeHelpers.formatUTCDate(lastReceived.timestamp) : '';
    const reportDate = DateTimeHelpers.formatUTCDate(noonReport.date!.timestamp);
    const preDate = DateTimeHelpers.formatUTCDate(moment(noonReport.date!.timestamp).subtract(1, 'days').unix() * 1000);
    const diff = ((sum - previousRob) > 10 && (lastReceivedDate === reportDate || preDate === lastReceivedDate)) ?
      sum - previousRob - lastReceived!.quantityMt : sum - previousRob;
    return {
      previousRob,
      consumption: noonReport.lubOilStatus!.mainEngineCylinderOilConsumption,
      rob: noonReport.lubOilStatus!.mainEngineCylinderOilRob,
      sum,
      diff: Math.round(diff * 100) / 100,
      lastReceivedQt: (lastReceivedDate === reportDate || preDate === lastReceivedDate) ? lastReceived!.quantityMt : ''
    };
  }

  calculateMainEngineSystemOilConsumption(noonReport: NoonReportV202005, previousReport: NoonReportV202005) {
    const previousRob = (previousReport) ? previousReport.lubOilStatus!.mainEngineSystemOilRob : noonReport.lubOilStatus!.mainEngineSystemOilRob;
    const sum = noonReport.lubOilStatus!.mainEngineSystemOilConsumption + noonReport.lubOilStatus!.mainEngineSystemOilRob;
    const lastReceived = noonReport.lubOilStatus!.lastReceived.find(l => l.receivedType === 'ME SYS Oil');
    const lastReceivedDate = lastReceived ? DateTimeHelpers.formatUTCDate(lastReceived.timestamp) : '';
    const reportDate = DateTimeHelpers.formatUTCDate(noonReport.date!.timestamp);
    const preDate = DateTimeHelpers.formatUTCDate(moment(noonReport.date!.timestamp).subtract(1, 'days').unix() * 1000);
    const diff = ((sum - previousRob) > 10 && (lastReceivedDate === reportDate || preDate === lastReceivedDate)) ?
      sum - previousRob - lastReceived!.quantityMt : sum - previousRob;
    return {
      previousRob,
      consumption: noonReport.lubOilStatus!.mainEngineSystemOilConsumption,
      rob: noonReport.lubOilStatus!.mainEngineSystemOilRob,
      sum,
      diff: Math.round(diff * 100) / 100,
      lastReceivedQt: (lastReceivedDate === reportDate || preDate === lastReceivedDate) ? lastReceived!.quantityMt : ''
    };
  }

  calculateOilCunsumption(noonReport: NoonReportV202005, previousReport: NoonReportV202005) {
    return {
      auxiliarySystemOil: this.calculateAuxiliarySystemOilConsumption(noonReport, previousReport),
      mainEngineCylinderOil: this.calculateMainEngineCylinderOilConsumption(noonReport, previousReport),
      mainEngineSystemOil: this.calculateMainEngineSystemOilConsumption(noonReport, previousReport),
    };
  }

  fuelConsumptionReport(noonReports: NoonReportV202005[]) {
    if (noonReports.length < 2){
      return;
    }
    const grouped = _.groupBy(noonReports, (item) => item.vesselImo);
    for (const l of Object.keys(grouped)) {
      grouped[l] = grouped[l].sort((a, b) => a.date!.timestamp - b.date!.timestamp);
    }
    return _(grouped).map((items, key) => {
      const analysis = items.map((noonReport, index) => {
        const previousReport = items[index - 1];
        return {
          date: DateTimeHelpers.formatUTCDate(noonReport.date!.timestamp),
          reportType: noonReport.reportType,
          consumptions: this.calculateTypeConsumption(noonReport, previousReport),
          oilConsumptions: this.calculateOilCunsumption(noonReport, previousReport)
        };

      });
      const consumptions = analysis.map((r:any) => ({
        hfoCons: r.consumptions['hfo'].consumptionChart,
        hfoRob: r.consumptions['hfo'].robChart,
        lsfoCons: r.consumptions['lsfo'].consumptionChart,
        lsfoRob: r.consumptions['lsfo'].robChart,
        mgoCons: r.consumptions['mgo'].consumptionChart,
        mgoRob: r.consumptions['mgo'].robChart,
        lsmgoCons: r.consumptions['lsmgo'].consumptionChart,
        lsmgoRob: r.consumptions['lsmgo'].robChart
      }));

      const diff = analysis.map((r:any) => ({
        hfoDiff: r.consumptions['hfo'].diff,
        lsfoDiff: r.consumptions['lsfo'].diff,
        mgoDiff: r.consumptions['mgo'].diff,
        lsmgoDiff: r.consumptions['lsmgo'].diff,
      }));

      const balances = [
        { id: 'hfoBalance', value: Math.round(diff.map(a => a.hfoDiff).reduce((a, b) => ((b) ? a + b : a + 0), 0) * 100) / 100 },
        { id: 'lsfoBalance', value: Math.round(diff.map(a => a.lsfoDiff).reduce((a, b) => ((b) ? a + b : a + 0), 0) * 100) / 100 },
        { id: 'mgoBalance', value: Math.round(diff.map(a => a.mgoDiff).reduce((a, b) => ((b) ? a + b : a + 0), 0) * 100) / 100 },
        { id: 'lsmgoBalance', value: Math.round(diff.map(a => a.lsmgoDiff).reduce((a, b) => ((b) ? a + b : a + 0), 0) * 100) / 100 },
      ].filter(b => b.value > 0);

      const highchartsData = this.groupedForHighCharts(consumptions);
      const sumConsumptions = highchartsData.filter(h => !h.id.includes('Rob')).map(h => ({
        id: h.id,
        sum: Math.round(this.sumConsumptions(h.series[0].data))
      }));
      return {
        vesselname: items[0].vesselName,
        vesselImo: key,
        distanceCovered: this.sumDistanceCovered(items),
        analysis,
        highchartsData,
        sumConsumptions,
        balances
      };
    }
    ).value()[0];
  }

  sumConsumptions(consumptions: [number, number][]) {
    return consumptions.reduce((a, b) => (a + b[1]), 0);
  }

  groupedForHighCharts(consumptions: any) {
    const charts: HighchartsData[] =
      [
        {
          id: 'hfoCons',
          divId: 'hfoConsDiv',
          series: [{
            name: 'HFO Consumption',
            type: 'line',
            visible: true,
            data: consumptions.map((c:any) => c.hfoCons)
          }]
        },
        {
          id: 'hfoRob',
          divId: 'hfoRobDiv',
          series: [
            {
              name: 'HFO ROB',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.hfoRob)
            }
          ]
        },
        {
          id: 'lsfoCons',
          divId: 'lsfoConsDiv',
          series: [
            {
              name: 'LSFO Consumption',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.lsfoCons)
            }]
        },
        {
          id: 'lsfoRob',
          divId: 'lsfoRobDiv',
          series: [
            {
              name: 'LSFO ROB',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.lsfoRob)
            }
          ]
        },
        {
          id: 'mgoCons',
          divId: 'mgoConsDiv',
          series: [
            {
              name: 'MGO Consumption',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.mgoCons)
            }
          ]
        },
        {
          id: 'mgoRob',
          divId: 'mgoRobDiv',
          series: [
            {
              name: 'MGO ROB',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.mgoRob)
            }
          ]
        },
        {
          id: 'lsmgoCons',
          divId: 'lsmgoConsDiv',
          series: [
            {
              name: 'LSMGO Consumption',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.lsmgoCons)
            }
          ]
        },
        {
          id: 'lsmgoRob',
          divId: 'lsmgoRobDiv',
          series: [
            {
              name: 'LSMGO ROB',
              type: 'line',
              visible: true,
              data: consumptions.map((c:any) => c.lsmgoRob)
            }
          ]
        }
      ];
    return charts.filter(s => this.sumConsumptions(s.series[0].data) > 0);
  }

  sumDistanceCovered(noonReports: NoonReportV202005[]) {
    return noonReports.map(a => a.voyageDetails!.distanceCovered).reduce((a, b) => (a + b), 0);
  }
}
